import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { QawaemErpModalFormTabBar } from '../../products/saudi/components/QawaemErpModalFormTabs';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Tag, Plus, Search, X, RefreshCcw, CheckCircle, AlertCircle, 
  Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, 
  Filter, Eye, LayoutGrid, Layers, Users, TrendingUp, FileText, 
  List, LayoutList, Shield
, Trash2 } from 'lucide-react';
import QawaemPageTitleWithFilters from '../../products/saudi/components/QawaemPageTitleWithFilters';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { useAppData } from '../../core/contexts/AppDataContext';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import { CustomDatePicker } from '../../shared/components/custom';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../core/styles/global-dropdowns.css';
import { useTranslation } from 'react-i18next';
import { SAUDI_DEBIT_CATEGORIES_FLAT, SAUDI_CREDIT_CATEGORIES_FLAT } from './saudiTreasuryDefaults';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import QawaemErpCrudModal, {
  QawaemErpViewModal,
  QAWAEM_MODAL_OVERLAY_CLASS,
  getQawaemModalPanelClass,
  getQawaemModalPanelStyle,
} from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { buildCaisseColumnDefs, useCaisseColumnVisibility, caisseTableColSpan, buildCaisseModalFormTabs, sliceCaisseListItems, fetchTreasuryOperationTypes, mapTreasuryOperationTypeToApi, dispatchCaisseOperationTypesRefresh, buildDefaultOperationCategories, CAISSE_FORM_GRID } from './caisseListPageUtils';
import localApiAdapter from '../../core/services/localApiAdapter';
import { useTreasuryConfig, useTreasuryListViewMode } from './useTreasuryConfig';
import { QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';

const OperationTypesPage = () => {
  const { t } = useTranslation();
  const treasuryConfig = useTreasuryConfig();
  const [viewMode, setViewMode] = useTreasuryListViewMode(treasuryConfig);
  const p = (key, opts) => t(`modules.caisse.pages.operationTypesPage.${key}`, opts);
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;
  const { caisseData, updateOperationTypes } = useAppData();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'نوع العملية' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'أنواع العمليات' });
  
  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  const [operationFormTab, setOperationFormTab] = useState('basic');

  const operationFormTabDefs = useMemo(() => ([
    { id: 'basic', label: p('section_basic_info'), icon: Tag, iconClassName: 'text-emerald-400' },
    { id: 'notes', label: p('section_additional'), icon: FileText, iconClassName: 'text-yellow-400' },
  ]), [p]);
  const OPERATION_TYPE_FIELD_COUNT = 5;
  const operationFormTabs = useMemo(
    () => buildCaisseModalFormTabs(operationFormTabDefs, OPERATION_TYPE_FIELD_COUNT),
    [operationFormTabDefs],
  );
  
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    category: '',
    description: '',
    status: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_operations');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  
  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  const columnFilterRules = useMemo(() => [
    textColumnRule('name', (row) => row.name),
    exactColumnRule('type', (row) => row.type),
    textColumnRule('category', (row) => row.category),
    exactColumnRule('status', (row) => row.status),
  ], []);

  const operationTypesColumnKeys = useMemo(
    () => ['name', 'type', 'category', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useCaisseColumnVisibility(operationTypesColumnKeys);
  const qawaemColumns = useMemo(
    () => buildCaisseColumnDefs([
      { key: 'name', labelKey: 'col_name' },
      { key: 'type', labelKey: 'col_type' },
      { key: 'category', labelKey: 'col_category' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, columnVisibility),
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = caisseTableColSpan(qawaemColumns);

  const debitCategories = SAUDI_DEBIT_CATEGORIES_FLAT;
  const creditCategories = SAUDI_CREDIT_CATEGORIES_FLAT;

  const categoryOptionsForType = useMemo(() => {
    if (formData.type === 'credit') {
      return creditCategories.map((c) => ({ value: c.name, label: c.name, isDefault: true }));
    }
    if (formData.type === 'debit') {
      return debitCategories.map((c) => ({ value: c.name, label: c.name, isDefault: true }));
    }
    return [];
  }, [formData.type, debitCategories, creditCategories]);

  const [operations, setOperations] = useState([]);
  const [loadingOperations, setLoadingOperations] = useState(false);

  const loadOperations = useCallback(async () => {
    setLoadingOperations(true);
    try {
      const rows = await fetchTreasuryOperationTypes();
      if (rows.length) {
        setOperations(sortRecordsNewestFirst(rows));
      } else {
        const fallback = buildDefaultOperationCategories();
        const seeded = [];
        let id = 1;
        fallback.operationCategories.debit.forEach((cat) => {
          cat.operations.forEach((opName) => {
            seeded.push({
              id: id++,
              name: opName,
              type: 'debit',
              category: cat.category,
              description: cat.category,
              status: 'active',
              created_at: new Date().toISOString(),
            });
          });
        });
        fallback.operationCategories.credit.forEach((cat) => {
          cat.operations.forEach((opName) => {
            seeded.push({
              id: id++,
              name: opName,
              type: 'credit',
              category: cat.category,
              description: cat.category,
              status: 'active',
              created_at: new Date().toISOString(),
            });
          });
        });
        setOperations(sortRecordsNewestFirst(seeded));
      }
    } finally {
      setLoadingOperations(false);
    }
  }, []);

  useEffect(() => {
    void loadOperations();
  }, [loadOperations]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const stats = {
    total: operations.length,
    debit: operations.filter(op => op.type === 'debit').length,
    credit: operations.filter(op => op.type === 'credit').length,
    active: operations.filter(op => op.status === 'active').length,
    inactive: operations.filter(op => op.status === 'inactive').length
  };

  const mainTabs = [
    { id: 'all_operations', nameKey: 'tab_general', icon: LayoutGrid },
    { id: 'by_type', nameKey: 'tab_by_type', icon: Shield },
    { id: 'by_category', nameKey: 'tab_by_category', icon: Layers },
    { id: 'by_status', nameKey: 'tab_by_status', icon: CheckCircle }
  ];

  const quickFilterButtons = [
    { id: 'all', nameKey: 'filter_all', icon: Layers },
    { id: 'debit', nameKey: 'filter_debit', icon: TrendingUp },
    { id: 'credit', nameKey: 'filter_credit', icon: CheckCircle },
    { id: 'active', nameKey: 'filter_active', icon: CheckCircle }
  ];

  const getTypeLabel = (type) => p(type === 'debit' ? 'type_debit' : 'type_credit');

  const getDistributionData = () => {
    switch(mainViewMode) {
      case 'by_type':
        return {
          titleKey: 'dist_by_type',
          data: [
            { filterKey: 'debit', nameKey: 'dist_debit', count: stats.debit, color: "bg-gradient-to-r from-red-500 to-red-600" },
            { filterKey: 'credit', nameKey: 'dist_credit', count: stats.credit, color: "bg-gradient-to-r from-green-500 to-green-600" }
          ]
        };
      case 'by_status':
        return {
          titleKey: 'dist_by_status',
          data: [
            { filterKey: 'active', nameKey: 'dist_active', count: stats.active, color: "bg-gradient-to-r from-green-500 to-green-600" },
            { filterKey: 'inactive', nameKey: 'dist_inactive', count: stats.inactive, color: "bg-gradient-to-r from-gray-500 to-gray-600" }
          ]
        };
      case 'by_category':
        const categoryCount = {};
        operations.forEach(op => {
          categoryCount[op.category] = (categoryCount[op.category] || 0) + 1;
        });
        return {
          titleKey: 'dist_by_category',
          data: Object.entries(categoryCount).map(([name, count], idx) => ({
            filterKey: name,
            nameKey: null,
            name,
            count,
            color: ["bg-gradient-to-r from-blue-500 to-blue-600", "bg-gradient-to-r from-purple-500 to-purple-600", "bg-gradient-to-r from-yellow-500 to-yellow-600", "bg-gradient-to-r from-pink-500 to-pink-600"][idx % 4]
          }))
        };
      default:
        return {
          titleKey: 'dist_overview',
          data: [
            { filterKey: null, nameKey: 'dist_total', count: stats.total, color: "bg-gradient-to-r from-blue-500 to-blue-600" },
            { filterKey: 'debit', nameKey: 'dist_debit', count: stats.debit, color: "bg-gradient-to-r from-red-500 to-red-600" },
            { filterKey: 'credit', nameKey: 'dist_credit', count: stats.credit, color: "bg-gradient-to-r from-green-500 to-green-600" },
            { filterKey: 'active', nameKey: 'dist_active', count: stats.active, color: "bg-gradient-to-r from-emerald-500 to-emerald-600" }
          ]
        };
    }
  };

  const distributionInfo = getDistributionData();

  const filteredOperations = applyColumnFilters(
    operations.filter(op => {
      if (searchTerm && !op.name?.toLowerCase().includes(searchTerm.toLowerCase()) && !op.category?.toLowerCase().includes(searchTerm.toLowerCase())) return false;

      if (quickFilter === 'debit' && op.type !== 'debit') return false;
      if (quickFilter === 'credit' && op.type !== 'credit') return false;
      if (quickFilter === 'active' && op.status !== 'active') return false;

      if (activeDistributionFilter) {
        if (activeDistributionFilter === 'debit' && op.type !== 'debit') return false;
        if (activeDistributionFilter === 'credit' && op.type !== 'credit') return false;
        if (activeDistributionFilter === 'active' && op.status !== 'active') return false;
        if (activeDistributionFilter === 'inactive' && op.status !== 'inactive') return false;
        if (!['debit', 'credit', 'active', 'inactive'].includes(activeDistributionFilter) && op.category !== activeDistributionFilter) return false;
      }

      return true;
    }),
    filters,
    columnFilterRules,
  );
  const pagedOperations = useMemo(
    () => sliceCaisseListItems(filteredOperations, treasuryConfig.itemsPerPage),
    [filteredOperations, treasuryConfig.itemsPerPage],
  );

  const {
    selectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord,
    clearSelection,
  } = useQawaemFilteredRowSelection(filteredOperations);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = mapTreasuryOperationTypeToApi(formData, {
        editingCode: editingRecord?.code,
      });
      if (editingRecord?.id && typeof editingRecord.id === 'number') {
        await localApiAdapter.put(`/treasury/operation-types/${editingRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/treasury/operation-types', payload);
        notifyCreated(showNotify, entityLabel);
      }
      dispatchCaisseOperationTypesRefresh();
      await loadOperations();
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('toast_save_error'));
    } finally {
      resetForm();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ ...record });
    setOperationFormTab('basic');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('delete_confirm'))) return;
    try {
      if (typeof id === 'number') {
        await localApiAdapter.delete(`/treasury/operation-types/${id}`);
      }
      dispatchCaisseOperationTypesRefresh();
      await loadOperations();
      notifyDeleted(showNotify, entityLabel);
    } catch (error) {
      console.error('Error:', error);
      notifyError(showNotify, p('toast_delete_error'));
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) return;
    try {
      const deleteCount = selectedRecords.length;
      await Promise.all(
        selectedRecords
          .filter((id) => typeof id === 'number')
          .map((id) => localApiAdapter.delete(`/treasury/operation-types/${id}`)),
      );
      clearSelection();
      dispatchCaisseOperationTypesRefresh();
      await loadOperations();
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
    } catch (error) {
      console.error('Error:', error);
      notifyError(showNotify, p('toast_delete_error'));
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', type: '', category: '', description: '', status: ''
    });
    setEditingRecord(null);
    setOperationFormTab('basic');
    setShowModal(false);
  };

  const getStatusBadge = (status) => {
    const config = clickableStatusOptions.find((s) => s.value === status) || clickableStatusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };

  return (
    <div className={`space-y-3 employees-container`}>
      <div className="flex justify-between items-center">
        <div className="flex-1 min-w-0">
          <QawaemPageTitleWithFilters
            filters={selectedRecords.length > 0 ? (
              <button
                type="button"
                onClick={handleBulkDelete}
                className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
                title={p('delete_selection')}
              >
                <Trash2 size={16} />
                <span>{t('common.delete')} ({selectedRecords.length})</span>
              </button>
            ) : null}
          >
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Tag className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadOperations} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          </QawaemPageTitleWithFilters>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{p('advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={() => { setEditingRecord(null); setOperationFormTab('basic'); setShowModal(true); }}>
            <Plus className="h-5 w-5" /><span>{p('new_operation')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: p('stat_total'), value: stats.total, icon: Tag, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_debit'), value: stats.debit, icon: TrendingUp, color: 'from-red-500 to-red-600' },
          { title: p('stat_credit'), value: stats.credit, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_active'), value: stats.active, icon: CheckCircle, color: 'from-emerald-500 to-emerald-600' },
          { title: p('stat_inactive'), value: stats.inactive, icon: AlertCircle, color: 'from-gray-500 to-gray-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_operations' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(filter.nameKey)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{p(distributionInfo.titleKey)}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <X size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => {
                const itemLabel = item.nameKey ? p(item.nameKey) : item.name;
                const itemFilter = item.filterKey ?? item.name;
                return (
                <button key={itemLabel} onClick={() => setActiveDistributionFilter(activeDistributionFilter === itemFilter ? null : itemFilter)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === itemFilter ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === itemFilter ? 'text-white' : 'text-white/80'}`}>{itemLabel}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === itemFilter ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              );})}
            </div>
          </motion.div>
        </div>
      </motion.div>

      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                    selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                    deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء التحديد' })}
                  />
                  {isColumnVisible('name') ? (
                    <TableColumnHeaderFilter
                      columnKey="name"
                      label={p('col_name')}
                      value={filters.name || ''}
                      onChange={(v) => setFilter('name', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white font-semibold"
                    />
                  ) : null}
                  {isColumnVisible('type') ? (
                    <TableColumnHeaderFilter
                      columnKey="type"
                      label={p('col_type')}
                      kind="select"
                      value={filters.type || ''}
                      onChange={(v) => setFilter('type', v)}
                      options={[
                        { value: 'debit', label: p('type_debit') },
                        { value: 'credit', label: p('type_credit') },
                      ]}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white font-semibold"
                    />
                  ) : null}
                  {isColumnVisible('category') ? (
                    <TableColumnHeaderFilter
                      columnKey="category"
                      label={p('col_category')}
                      value={filters.category || ''}
                      onChange={(v) => setFilter('category', v)}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white font-semibold"
                    />
                  ) : null}
                  {isColumnVisible('status') ? (
                    <TableColumnHeaderFilter
                      columnKey="status"
                      label={p('col_status')}
                      kind="select"
                      align="center"
                      value={filters.status || ''}
                      onChange={(v) => setFilter('status', v)}
                      options={[
                        { value: 'active', label: p('status_active') },
                        { value: 'inactive', label: p('status_inactive') },
                      ]}
                      editingKey={editingKey}
                      onEditingKeyChange={setEditingKey}
                      thClassName="text-white font-semibold"
                    />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredOperations.length > 0 ? (
                  pagedOperations.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(record.id)}
                        onChange={() => handleSelectRecord(record.id)}
                      />
                      {isColumnVisible('name') ? (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.name}</span></td>
                      ) : null}
                      {isColumnVisible('type') ? (
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${record.type === 'debit' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                            {getTypeLabel(record.type)}
                          </span>
                        </td>
                      ) : null}
                      {isColumnVisible('category') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{record.category || '-'}</span></td>
                      ) : null}
                      {isColumnVisible('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge
                              status={record.status}
                              recordId={record.id}
                              options={clickableStatusOptions}
                              onChange={handleStatusChange}
                              title={t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                            />
                          </div>
                        </td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Tag size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_no_operations')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_total', { count: filteredOperations.length })}</span>
                      <span>{p('footer_debit')}: <span className="text-white font-semibold">{filteredOperations.filter(op => op.type === 'debit').length}</span></span>
                      <span>{p('footer_credit')}: <span className="text-white font-semibold">{filteredOperations.filter(op => op.type === 'credit').length}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredOperations.length > 0 ? (
                pagedOperations.map((record, index) => (
                  <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Tag size={18} className="text-emerald-400" />
                        {record.name}
                      </h3>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-xs mb-1">{p('col_type')}</p>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${record.type === 'debit' ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                          {getTypeLabel(record.type)}
                        </span>
                      </div>
                      
                      <div>
                        <p className="text-white/60 text-xs mb-1">{p('col_category')}</p>
                        <p className="text-white text-sm">{record.category || '-'}</p>
                      </div>
                      
                      <div>
                        <p className="text-white/60 text-xs mb-1">{p('col_status')}</p>
                        <QawaemClickableStatusBadge
                          status={record.status}
                          recordId={record.id}
                          options={clickableStatusOptions}
                          onChange={handleStatusChange}
                        />
                      </div>
                      
                      {record.description && (
                        <div>
                          <p className="text-white/60 text-xs mb-1">{p('label_description')}</p>
                          <p className="text-white/70 text-xs">{record.description}</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-1 mt-4 pt-4 border-t border-white/10">
                      <QawaemErpRowActions
                        compact
                        size={14}
                        className="flex-1 justify-around"
                        onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="col-span-4 text-center py-12"><Tag size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_no_operations')}</p></div>
              )}
            </div>
          </div>
        )}
      </div>

      {showModal && ReactDOM.createPortal(
        <div className={QAWAEM_MODAL_OVERLAY_CLASS} onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={getQawaemModalPanelClass(isLight)} style={getQawaemModalPanelStyle(isLight, '1200px')} onClick={(e) => e.stopPropagation()}>
            <div className={`flex items-center justify-between p-6 border-b shrink-0 ${isLight ? 'border-slate-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${cfgTheme.title}`}>{editingRecord ? p('modal_edit') : p('modal_new')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="operation-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{editingRecord ? p('btn_update') : p('btn_create')}</button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            {operationFormTabs.length > 0 ? (
              <QawaemErpModalFormTabBar
                tabs={operationFormTabs}
                activeTab={operationFormTab}
                onTabChange={setOperationFormTab}
                isLight={isLight}
                ariaLabel={editingRecord ? p('modal_edit') : p('modal_new')}
              />
            ) : null}
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="operation-form" onSubmit={handleSubmit}>
                {(operationFormTabs.length === 0 || operationFormTab === 'basic') && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_name')} <span className="qawaem-required-mark">*</span></label>
                      <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_name')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_type')} <span className="qawaem-required-mark">*</span></label>
                      <CustomSelect value={formData.type} onChange={(value) => setFormData({ ...formData, type: value })} options={[
                        { value: 'debit', label: p('type_debit') },
                        { value: 'credit', label: p('type_credit') }
                      ]} searchable={true} creatable={true} autoSelect={false} placeholder={p('placeholder_type')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_category')}</label>
                      <CustomSelect value={formData.category} onChange={(value) => setFormData({ ...formData, category: value })} options={categoryOptionsForType} searchable={true} creatable={true} autoSelect={false} placeholder={p('placeholder_category')} />
                    </div>
                    <div className="min-w-0">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')} <span className="qawaem-required-mark">*</span></label>
                      <CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={[
                        { value: 'active', label: p('status_active'), icon: <CheckCircle size={14} className="text-green-400" /> },
                        { value: 'inactive', label: p('status_inactive'), icon: <AlertCircle size={14} className="text-gray-400" /> }
                      ]} searchable={false} creatable={false} autoSelect={false} />
                    </div>
                    <div className="min-w-0 md:col-span-3">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_description')}</label>
                      <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_description')} />
                    </div>
                  </div>
                )}
                {operationFormTabs.length > 0 && operationFormTab === 'notes' && (
                  <div className={CAISSE_FORM_GRID}>
                    <div className="min-w-0 md:col-span-3">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_description')}</label>
                      <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_description')} />
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open={showViewModal}
        onClose={() => { setShowViewModal(false); setViewingRecord(null); }}
        title={p('view_title') || p('modal_edit')}
        icon={Eye}
        maxWidth="720px"
        testId="operation-type-view-modal"
      >
          <div className="space-y-3">
            {[
              { key: 'name', label: p('label_name') },
              { key: 'type', label: p('label_type') },
              { key: 'category', label: p('label_category') },
              { key: 'status', label: p('label_status') },
              { key: 'description', label: p('label_description') },
            ].map(({ key, label }) => (
              <div
                key={key}
                className={`flex justify-between gap-4 border-b pb-2 ${isLight ? 'border-slate-100' : 'border-white/5'}`}
              >
                <span className={`text-sm shrink-0 ${cfgTheme.subtitle}`}>{label}</span>
                <span className={`text-sm text-end ${cfgTheme.title}`}>{viewingRecord[key] ?? '—'}</span>
              </div>
            ))}
          </div>
      </QawaemErpViewModal>
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default OperationTypesPage;


